import { useQuerystring } from "$routes/_lib/use-querystring";
import { gql } from "@apollo/client";
import type {
  DispensationSyncPushInput,
  QueryPullDispensationArgs,
  SyncableDispensation,
} from "elephant-web/gql";
import { useMemo } from "react";
import { useMatch } from "react-router-dom";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { Dispensation } from "../collections/dispensation";
import type { RxDBCollections } from "../provider";
import {
  SyncDispensationPullDocument,
  SyncDispensationPushDocument,
  type WholeSyncableDispensationFragment,
} from "./__generated__/dispensation.gql";
import { createPersistedCheckpointPullBuilders } from "./create-persisted-checkpoint-pull-builders";
import {
  type ReplicationPullConfig,
  useGqlReplication,
} from "./use-gql-replication";

/**
 * Replicates dispensations to and from the server.
 *
 * pull: https://elephant.slab.com/posts/just-in-time-sync-pattern-wip-grv582xm
 */
export const useReplicateDispensation = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<Dispensation>;
}) => {
  const query = useQuerystring();
  const matchPat = useMatch("/dev-patient/:patientId/*");
  const patientId = matchPat?.params.patientId ?? query.patientUuid;
  const push = useMemo(
    () => ({
      mutation: SyncDispensationPushDocument,
      mapRxToGql: mapRxDispensationToGqlDispensationInput,
    }),
    [],
  );

  const pull = useMemo<ReplicationPullConfig<SyncableDispensation>>(() => {
    return patientId
      ? {
          query: SyncDispensationPullDocument,
          reSyncInterval: { seconds: 30 },
          ...createPersistedCheckpointPullBuilders<
            QueryPullDispensationArgs,
            SyncableDispensation
          >({
            buildInputVariables: (checkpoint, batchSize) => ({
              input: { limit: batchSize, filters: { patientId }, checkpoint },
            }),
            buildPullCheckpoint: (doc) => ({
              id: doc.id,
              updatedAt: doc.meta.updatedAt,
            }),
            entityId: patientId,
            db,
            collectionName: collection?.name,
          }),
        }
      : undefined;
  }, [patientId, db, collection]);

  return useGqlReplication<
    Dispensation,
    SyncableDispensation,
    DispensationSyncPushInput
  >({
    db,
    collection,
    push,
    pull,
    mapGqlToRx: mapGqlSyncableDispensationToRxDispensation,
    batchSize: 100,
    expireData: true,
    metadataLocalStorageKeySuffix: `dispensation-${patientId}`,
  });
};

export const mapRxDispensationToGqlDispensationInput = (
  dispensation: WithDeleted<Dispensation>,
): DispensationSyncPushInput => {
  return {
    id: dispensation.id,
    dispensedByUserId: dispensation.dispensedByUserId,
    dispensationStrategy: dispensation.dispensationStrategy,
    facilityId: dispensation.facilityId,
    locationId: dispensation.locationId,
    meta: {
      createdAt: dispensation.meta.createdAt,
      updatedAt: dispensation.meta.updatedAt,
      createdByUserId: dispensation.meta.createdByUserId,
      updatedByUserId: dispensation.meta.updatedByUserId,
      deleted: dispensation._deleted,
    },
    prescriptionId: dispensation.prescriptionId,
    quantity: dispensation.quantity ?? 0, // TODO: ENG-4372 don't nullish coalesce once optional quantity rolled out
    skuId: dispensation?.skuId,
  };
};

export const mapGqlSyncableDispensationToRxDispensation = (
  dispensation: WholeSyncableDispensationFragment,
): WithDeleted<Dispensation> => {
  return {
    id: dispensation.id,
    dispensationStrategy: dispensation.dispensationStrategy,
    dispensedByUserId: dispensation.dispensedByUserId,
    facilityId: dispensation.facilityId,
    locationId: dispensation.locationId,
    meta: {
      createdAt: dispensation.meta.createdAt,
      updatedAt: dispensation.meta.updatedAt,
      createdByUserId: dispensation.meta.createdByUserId,
      updatedByUserId: dispensation.meta.updatedByUserId,
    },
    prescriptionId: dispensation.prescriptionId,
    quantity: dispensation.quantity,
    skuId: dispensation?.skuId,
    _deleted: dispensation.meta.deleted,
  };
};

gql`
  fragment WholeSyncableDispensation on SyncableDispensation {
    id
    dispensationStrategy
    dispensedByUserId
    facilityId
    locationId
    meta {
      createdAt
      createdByUserId
      deleted
      updatedAt
      updatedByUserId
    }
    prescriptionId
    quantity
    skuId
  }
`;

gql`
  mutation SyncDispensationPush($input: PushDispensationInput!) {
    pushDispensation(input: $input) {
      rows {
        ...WholeSyncableDispensation
      }
    }
  }
`;

gql`
  query SyncDispensationPull($input: PullDispensationInput!) {
    pullDispensation(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncableDispensation
      }
    }
  }
`;
